import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import {
  LayoutGrid, User, CreditCard, BookOpen, Heart, Bell, Settings, Briefcase, Crown, Users, Inbox, ScanLine,
  ArrowLeft, ExternalLink, LogOut, Loader2, Lock, Shield,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import LangSwitcher from "@/components/LangSwitcher";

const PREMIUM_TIERS = new Set(["gold", "vip", "corporate"]);

export function accountNav(t) {
  return [
    { to: "/account", label: t("vc.acct.nav.overview"), tab: t("vc.acct.tab.overview"), icon: LayoutGrid, index: "01" },
    { to: "/account/profile", label: t("vc.acct.nav.profile"), icon: User, index: "02" },
    { to: "/account/card", label: t("vc.acct.nav.card"), tab: t("vc.acct.tab.card"), icon: CreditCard, index: "03" },
    { to: "/magazine", label: t("vc.acct.nav.magazine"), icon: BookOpen, index: "04", gate: "approved" },
    { to: "/matching", label: t("vc.acct.nav.matching"), icon: Heart, index: "05", gate: "premium" },
    { to: "/account/directory", label: t("vc.acct.nav.directory"), tab: t("vc.acct.tab.directory"), icon: Users, index: "06", gate: "approved" },
    { to: "/account/concierge", label: t("vc.acct.nav.requests"), icon: Bell, index: "07" },
    { to: "/account/services", label: t("vc.acct.nav.services"), tab: t("vc.acct.tab.services"), icon: Briefcase, index: "08" },
    { to: "/account/membership", label: t("vc.acct.nav.plans"), icon: Crown, index: "09" },
    { to: "/account/settings", label: t("vc.acct.nav.settings"), tab: t("vc.acct.tab.settings"), icon: Settings, index: "10" },
  ];
}

export function isGateLocked(item, user) {
  if (!item.gate || !user) return false;
  const tier = (user.membership_tier || "silver").toLowerCase();
  const approved = user.application_status === "approved";
  if (item.gate === "approved") return !approved;
  if (item.gate === "premium") return !approved || !PREMIUM_TIERS.has(tier);
  return false;
}

const BOTTOM_KEYS = ["/account", "/account/card", "/account/directory", "/account/services", "/account/settings"];

export default function AccountShell({ children, title, active }) {
  const { user, loading, initialized, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (initialized && !loading && !user) navigate("/login", { replace: true });
  }, [initialized, loading, user, navigate]);

  if (!initialized || loading || !user) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center" data-testid="account-shell-loading">
        <Loader2 className="animate-spin" size={22} />
      </div>
    );
  }

  const nav = accountNav(t);
  const current = active || location.pathname;
  const tier = (user.membership_tier || "silver").toLowerCase();
  const bottom = BOTTOM_KEYS.map((k) => nav.find((n) => n.to === k));

  return (
    <div className="min-h-screen bg-[#050505] text-white marble-bg" data-testid="account-shell">
      <Helmet>
        <title>{`${title || t("vc.acct.title")} · VangardClub`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Top bar */}
      <header className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-[#050505]/80 border-b border-white/[0.06]">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 h-[60px] flex items-center gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 -ml-2 px-2 py-2 rounded-full text-[11px] tracking-[0.25em] uppercase text-neutral-500 hover:text-white transition-colors"
            data-testid="account-back-site"
          >
            <ArrowLeft size={14} /> <span className="hidden sm:inline">{t("vc.acct.back-site")}</span>
          </Link>
          <div className="w-px h-5 bg-white/10 hidden sm:block" />
          <Link to="/account" className="font-serif text-[18px] leading-none tracking-tight py-2">
            Vangard<span className="text-neutral-500">Club</span>
          </Link>
          <div className="flex-1" />
          <LangSwitcher />
          {user.slug && (
            <a
              href={`/u/${user.slug}`}
              target="_blank"
              rel="noreferrer"
              className="hidden md:inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] uppercase text-neutral-500 hover:text-white transition-colors"
              data-testid="account-view-public"
            >
              {t("vc.account.view-public")} <ExternalLink size={11} />
            </a>
          )}
        </div>

        {/* Mobile pill sub-nav */}
        <div className="lg:hidden relative border-t border-white/[0.06]">
          <div className="overflow-x-auto no-scrollbar" style={{ scrollSnapType: "x proximity" }}>
          <div className="flex items-center gap-2 px-5 py-2.5 min-w-max">
            {nav.map((n) => {
              const locked = isGateLocked(n, user);
              const on = current === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`acct-pill-${n.to.replace(/\//g, "-").slice(1)}`}
                  className={`inline-flex items-center gap-1.5 shrink-0 rounded-full px-3.5 py-2 text-[10px] tracking-[0.2em] uppercase whitespace-nowrap transition-colors ${
                    on ? "bg-white text-black" : "text-neutral-400 border border-white/[0.1] hover:text-white hover:border-white/30"
                  }`}
                >
                  {n.label}
                  {locked && <Lock size={9} className={on ? "text-black/60" : "text-neutral-600"} />}
                </Link>
              );
            })}
          </div>
          </div>
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-[#050505] to-transparent" />
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 pt-[124px] lg:pt-[92px] pb-32 lg:pb-20">
        <div className="lg:grid lg:grid-cols-[248px_1fr] lg:gap-14">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-[92px]">
              <div className="rounded-3xl border border-white/[0.08] bg-[#0A0A0A]/80 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full overflow-hidden bg-white/[0.06] flex items-center justify-center shrink-0">
                    {user.picture
                      ? <img src={user.picture} alt="" className="w-full h-full object-cover" />
                      : <span className="font-serif text-lg">{(user.name || "?").slice(0, 1).toUpperCase()}</span>}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-light truncate">{user.name}</div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mt-0.5">
                      {tier} · #{user.member_id}
                    </div>
                  </div>
                </div>
              </div>

              <nav className="mt-4 flex flex-col" data-testid="account-sidebar">
                {nav.map((n) => {
                  const on = current === n.to;
                  const locked = isGateLocked(n, user);
                  return (
                    <Link
                      key={n.to}
                      to={n.to}
                      data-testid={`acct-nav-${n.to.replace(/\//g, "-").slice(1)}`}
                      className={`group flex items-center gap-3 rounded-2xl px-4 py-3 transition-colors ${
                        on ? "bg-white/[0.07] text-white" : "text-neutral-400 hover:text-white hover:bg-white/[0.03]"
                      }`}
                    >
                      <n.icon size={15} strokeWidth={1.6} />
                      <span className="text-[11px] tracking-[0.2em] uppercase flex-1">{n.label}</span>
                      {locked && <Lock size={11} className="text-neutral-600" />}
                      {on && <span className="w-1 h-1 rounded-full bg-white" />}
                    </Link>
                  );
                })}

                {user.is_admin && (
                  <>
                    <div className="h-px bg-white/[0.06] my-4" />
                    <Link
                      to="/admin/scan"
                      className="flex items-center gap-3 rounded-2xl px-4 py-3 text-neutral-400 hover:text-white hover:bg-white/[0.03] transition-colors"
                      data-testid="acct-nav-admin-scan"
                    >
                      <ScanLine size={15} strokeWidth={1.6} />
                      <span className="text-[11px] tracking-[0.2em] uppercase">{t("vc.scan.nav")}</span>
                    </Link>
                    <Link
                      to="/admin/inbox"
                      className="flex items-center gap-3 rounded-2xl px-4 py-3 text-neutral-400 hover:text-white hover:bg-white/[0.03] transition-colors"
                      data-testid="acct-nav-admin-inbox"
                    >
                      <Inbox size={15} strokeWidth={1.6} />
                      <span className="text-[11px] tracking-[0.2em] uppercase">{t("vc.acct.nav.admin-inbox")}</span>
                    </Link>
                    <Link
                      to="/admin/applications"
                      className="flex items-center gap-3 rounded-2xl px-4 py-3 text-neutral-400 hover:text-white hover:bg-white/[0.03] transition-colors"
                      data-testid="acct-nav-admin-applications"
                    >
                      <Shield size={15} strokeWidth={1.6} />
                      <span className="text-[11px] tracking-[0.2em] uppercase">{t("vc.acct.nav.admin-apps")}</span>
                    </Link>
                    <Link
                      to="/admin/members"
                      className="flex items-center gap-3 rounded-2xl px-4 py-3 text-neutral-400 hover:text-white hover:bg-white/[0.03] transition-colors"
                      data-testid="acct-nav-admin-members"
                    >
                      <Shield size={15} strokeWidth={1.6} />
                      <span className="text-[11px] tracking-[0.2em] uppercase">{t("vc.acct.nav.admin-members")}</span>
                    </Link>
                  </>
                )}

                <div className="h-px bg-white/[0.06] my-4" />
                <button
                  onClick={async () => { await logout(); navigate("/"); }}
                  className="flex items-center gap-3 rounded-2xl px-4 py-3 text-neutral-500 hover:text-white hover:bg-white/[0.03] transition-colors"
                  data-testid="acct-logout"
                >
                  <LogOut size={15} strokeWidth={1.6} />
                  <span className="text-[11px] tracking-[0.2em] uppercase">{t("menu.logout")}</span>
                </button>
              </nav>
            </div>
          </aside>

          <main className="min-w-0">{children}</main>
        </div>
      </div>

      {/* Mobile bottom tab bar */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 backdrop-blur-xl bg-[#050505]/90 border-t border-white/[0.08]"
        data-testid="account-bottom-nav"
      >
        <div className="flex items-stretch justify-around px-1 py-2.5" style={{ paddingBottom: "calc(0.625rem + env(safe-area-inset-bottom))" }}>
          {bottom.map((n) => {
            const on = current === n.to;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`acct-tab-${n.to.replace(/\//g, "-").slice(1)}`}
                className={`flex flex-col items-center gap-1 px-3 py-1 transition-colors ${on ? "text-white" : "text-neutral-500"}`}
              >
                <n.icon size={19} strokeWidth={1.6} />
                <span className="text-[8px] tracking-[0.1em] uppercase whitespace-nowrap">{n.tab || n.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
